import { KeyboardEvent, ReactNode, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { tokens } from '../theme/tokens';

const HAIRLINE = 1;

export type SegmentedControlOption = {
	title: string;
	value: string;
	icon?: ReactNode;
	disabled?: boolean;
};

const Container = styled.div`
	display: flex;
	align-items: stretch;
	width: fit-content;
	gap: ${HAIRLINE}px;
	box-sizing: border-box;
	height: 33px;
	border: ${HAIRLINE}px solid ${tokens.border.default};
	border-radius: ${tokens.radius.sm}px;
	background: ${tokens.border.default};
	box-shadow: ${tokens.shadow.xs};
`;

const Segment = styled.button<{ $selected?: boolean }>`
	display: flex;
	align-items: center;
	flex: 0 0 auto;
	gap: ${tokens.gap.inline}px;
	height: 100%;
	margin: 0;
	padding: 0 ${tokens.gap.list}px;
	border: 0;
	appearance: none;
	position: relative;
	background: ${(props) => (props.$selected ? tokens.surface.secondary : tokens.surface.white)};
	color: ${(props) => (props.$selected ? tokens.text.primary : tokens.text.secondary)};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: 1;
	white-space: nowrap;
	cursor: pointer;

	&:first-child {
		border-top-left-radius: ${tokens.radius.sm - HAIRLINE}px;
		border-bottom-left-radius: ${tokens.radius.sm - HAIRLINE}px;
	}

	&:last-child {
		border-top-right-radius: ${tokens.radius.sm - HAIRLINE}px;
		border-bottom-right-radius: ${tokens.radius.sm - HAIRLINE}px;
	}

	&:disabled {
		background: ${tokens.surface.white};
		color: ${tokens.text.disabled};
		cursor: default;
	}

	${(props) =>
		!props.$selected &&
		css`
			&:hover:not(:disabled) {
				background: ${tokens.surface.hover};
				color: ${tokens.text.primary};
			}
		`}

	&:focus {
		outline: none;
	}

	&:focus-visible {
		outline: 2px solid ${tokens.border.focus};
		outline-offset: 2px;
		z-index: 1;
	}

	svg {
		display: block;
		width: 16px;
		height: 16px;
		flex-shrink: 0;
		color: inherit;
	}
`;

export function SegmentedControl(props: {
	value: string;
	options: SegmentedControlOption[];
	onChange: (value: string) => void;
	disabled?: boolean;
}) {
	const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const selectedIndex = props.options.findIndex((option) => option.value === props.value);

	function isEnabled(option: SegmentedControlOption) {
		return !props.disabled && !option.disabled;
	}

	const selectedIsEnabled = selectedIndex >= 0 && isEnabled(props.options[selectedIndex]);
	const selectedFocusIndex = selectedIsEnabled
		? selectedIndex
		: props.options.findIndex((option) => isEnabled(option));
	const focusedOption = focusedIndex == null ? undefined : props.options[focusedIndex];
	const focusIndex = focusedOption && isEnabled(focusedOption) ? focusedIndex : selectedFocusIndex;

	function focusAt(index: number) {
		setFocusedIndex(index);
		segmentRefs.current[index]?.focus();
	}

	function selectAt(index: number) {
		const option = props.options[index];
		if (!option || props.disabled || option.disabled) {
			return;
		}

		props.onChange(option.value);
		focusAt(index);
	}

	function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
		const nextKey = event.key === 'ArrowRight' || event.key === 'ArrowDown';
		const previousKey = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
		if (!nextKey && !previousKey) {
			return;
		}

		event.preventDefault();
		const direction = nextKey ? 1 : -1;
		const count = props.options.length;

		for (let step = 1; step <= count; step += 1) {
			const next = (index + direction * step + count) % count;
			const option = props.options[next];
			if (props.disabled || option.disabled) {
				continue;
			}

			focusAt(next);
			return;
		}
	}

	return (
		<Container role="radiogroup">
			{props.options.map((option, index) => {
				const disabled = !isEnabled(option);
				const selected = option.value === props.value && !disabled;
				const tabIndex = index === focusIndex ? 0 : -1;

				return (
					<Segment
						key={option.value}
						type="button"
						role="radio"
						aria-checked={selected}
						disabled={disabled}
						tabIndex={tabIndex}
						$selected={selected}
						ref={(node) => {
							segmentRefs.current[index] = node;
						}}
						onClick={() => selectAt(index)}
						onKeyDown={(event) => onKeyDown(event, index)}
					>
						{option.icon}
						{option.title}
					</Segment>
				);
			})}
		</Container>
	);
}
